import { useForm } from "react-hook-form";
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Plan } from "../../../service/model/plan.model";
import { Box, Button, Dialog, DialogTitle, Grid } from "@mui/material";
import FormField from "../../../components/form-controls/form-field";
import theme from "../../../app/app-theme";
import FormCloseButton from "../../../components/form-controls/form-close-button";
import { FormTextField } from "../../../components/form-input-field-wrappers/form-controller-inputs/form-text-field";

interface PlanFormProps {
    readonly plan?: Plan
    readonly editable: boolean
    readonly onClose: () => void
    readonly onSubmit: (data: Plan) => void
}

const style = {
    // position: 'absolute' as 'absolute',
    // top: '50%',
    // left: '50%',
    // transform: 'translate(-50%, -50%)',
    // width: 400,
    // bgcolor: 'background.paper',
    // border: '2px solid #000',
    // boxShadow: 24,
    p: 2,
};

const PlanForm: React.FC<PlanFormProps> = ({ plan, editable, onClose, onSubmit }) => {
    const schema = yup.object().shape({
        name: yup.string()
            .required(),
        price: yup.number()
            .transform((value) => Number.isNaN(value) ? null : value)
            .required("Price is required")
            .min(0),
        memberLimit: yup.number()
            .transform((value) => Number.isNaN(value) ? null : value)
            .required("Member limit is required")
            .min(0),
        messageLimit: yup.number()
            .transform((value) => Number.isNaN(value) ? null : value)
            .required("Message limit is required")
            .min(0),
        userAccountLimit: yup.number()
            .transform((value) => Number.isNaN(value) ? null : value)
            .required("User account limit is required")
            .min(0),
        storeProductLimit: yup.number()
            .transform((value) => Number.isNaN(value) ? null : value)
            .required("Store product limit is required")
            .min(0),
        extraMessageCost: yup.number()
            .transform((value) => Number.isNaN(value) ? null : value)
            .required("Message cost is required")
            .min(0),
        extraUserAccountCost: yup.number()
            .transform((value) => Number.isNaN(value) ? null : value)
            .required("User Account cost is required")
            .min(0),
        extraStoreProductCost: yup.number()
            .transform((value) => Number.isNaN(value) ? null : value)
            .required("Product cost is required")
            .min(0),
        type: yup.string()
    });

    const { register, handleSubmit, formState: { errors } } = useForm({
        resolver: yupResolver(schema),
        defaultValues: {
            type: plan?.type || 'per_member',
        }
    });

    const onSubmitHandler = (data: any) => {
        onSubmit(data as Plan)
    };

    return (
        <Dialog open={true} onClose={onClose} sx={style} fullWidth maxWidth="md">
            <DialogTitle>Plan Details</DialogTitle>
            <Box sx={style}>
                <>
                    <FormCloseButton onClose={onClose} />
                    <form onSubmit={handleSubmit(onSubmitHandler)}>
                        <Grid container direction="row" className="mt-0 my-0" justifyContent='space-between'>
                        <FormField>
                            <FormTextField
                                id="plan-name"
                                label="Plan Name"
                                name="name"
                                defaultValue={plan?.name}
                                errorMessage={errors.name?.message}
                                register={register}
                            />
                        </FormField>
                        <FormField>
                            <FormTextField
                                id="plan-price"
                                type="number"
                                name="price"
                                label="Price"
                                variant="outlined"
                                defaultValue={plan?.price}
                                errorMessage={errors.price?.message}
                                register={register}
                            />
                        </FormField>
                        <FormField>
                            <FormTextField
                                id="message-limit"
                                type="number"
                                name="messageLimit"
                                label="Message Limit"
                                variant="outlined"
                                defaultValue={plan?.messageLimit}
                                errorMessage={errors.messageLimit?.message}
                                register={register}
                            />
                        </FormField>
                        <FormField>
                            <FormTextField
                                id="extra-message-cost"
                                type="number"
                                name="extraMessageCost"
                                label="Message Cost"
                                variant="outlined"
                                defaultValue={plan?.extraMessageCost}
                                errorMessage={errors.extraMessageCost?.message}
                                register={register}
                            />
                        </FormField>
                        <FormField>
                            <FormTextField
                                id="user-account-limit"
                                type="number"
                                name="userAccountLimit"
                                label="User Account Limit"
                                variant="outlined"
                                defaultValue={plan?.userAccountLimit}
                                errorMessage={errors.userAccountLimit?.message}
                                register={register}
                            />
                        </FormField>
                        <FormField>
                            <FormTextField
                                id="extra-account-cost"
                                type="number"
                                name="extraUserAccountCost"
                                label="Account Cost"
                                variant="outlined"
                                defaultValue={plan?.extraUserAccountCost}
                                errorMessage={errors.extraUserAccountCost?.message}
                                register={register}
                            />
                        </FormField>
                        <FormField>
                            <FormTextField
                                id="store-product-limit"
                                type="number"
                                name="storeProductLimit"
                                label="Store Product Limit"
                                variant="outlined"
                                defaultValue={plan?.storeProductLimit}
                                errorMessage={errors.storeProductLimit?.message}
                                register={register}
                            />
                        </FormField>
                        <FormField>
                            <FormTextField
                                id="extra-product-cost"
                                type="number"
                                name="extraStoreProductCost"
                                label="Product Cost"
                                variant="outlined"
                                defaultValue={plan?.extraStoreProductCost}
                                errorMessage={errors.extraStoreProductCost?.message}
                                register={register}
                            />
                        </FormField>
                        <FormField>
                            <FormTextField
                                id="member-limit"
                                type="number"
                                name="memberLimit"
                                label="Member Limit"
                                variant="outlined"
                                defaultValue={plan?.memberLimit}
                                errorMessage={errors.memberLimit?.message}
                                register={register}
                            />
                        </FormField>
                        </Grid>
                        <Grid container direction="row" justifyContent='end' sx={{ mt: 1 }}>
                            <Button type="button" variant="contained" sx={{ mr: 1, bgcolor: `${theme.palette.secondary.main}` }} onClick={onClose}>Cancel</Button>
                            <Button type="submit" variant="contained" >Submit</Button>
                        </Grid>
                    </form>
                </>
            </Box>
        </Dialog>
    );
}

export default PlanForm;